import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Row, Col, FormText } from 'reactstrap';
import { isNumber, Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IMealType } from 'app/shared/model/meal-type.model';
import { getEntities as getMealTypes } from 'app/entities/meal-type/meal-type.reducer';
import { ICusine } from 'app/shared/model/cusine.model';
import { getEntities as getCusines } from 'app/entities/cusine/cusine.reducer';
import { IActivity } from 'app/shared/model/activity.model';
import { getEntities as getActivities } from 'app/entities/activity/activity.reducer';
import { IDiningDetails } from 'app/shared/model/dining-details.model';
import { getEntity, updateEntity, createEntity, reset } from './dining-details.reducer';

export const DiningDetailsUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const mealTypes = useAppSelector(state => state.mealType.entities);
  const cusines = useAppSelector(state => state.cusine.entities);
  const activities = useAppSelector(state => state.activity.entities);
  const diningDetailsEntity = useAppSelector(state => state.diningDetails.entity);
  const loading = useAppSelector(state => state.diningDetails.loading);
  const updating = useAppSelector(state => state.diningDetails.updating);
  const updateSuccess = useAppSelector(state => state.diningDetails.updateSuccess);

  const handleClose = () => {
    navigate('/dining-details' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getMealTypes({}));
    dispatch(getCusines({}));
    dispatch(getActivities({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    values.createdDate = convertDateTimeToServer(values.createdDate);
    values.modifiedDate = convertDateTimeToServer(values.modifiedDate);

    const entity = {
      ...diningDetailsEntity,
      ...values,
      mealTypes: mapIdList(values.mealTypes),
      cusines: mapIdList(values.cusines),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          createdDate: displayDefaultDateTime(),
          modifiedDate: displayDefaultDateTime(),
        }
      : {
          ...diningDetailsEntity,
          createdDate: convertDateTimeFromServer(diningDetailsEntity.createdDate),
          modifiedDate: convertDateTimeFromServer(diningDetailsEntity.modifiedDate),
          mealTypes: diningDetailsEntity?.mealTypes?.map(e => e.id.toString()),
          cusines: diningDetailsEntity?.cusines?.map(e => e.id.toString()),
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="activitiesApp.diningDetails.home.createOrEditLabel" data-cy="DiningDetailsCreateUpdateHeading">
            <Translate contentKey="activitiesApp.diningDetails.home.createOrEditLabel">Create or edit a DiningDetails</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="dining-details-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('activitiesApp.diningDetails.name')}
                id="dining-details-name"
                name="name"
                data-cy="name"
                type="text"
              />
              <ValidatedField
                label={translate('activitiesApp.diningDetails.about')}
                id="dining-details-about"
                name="about"
                data-cy="about"
                type="text"
              />
              <ValidatedField
                label={translate('activitiesApp.diningDetails.priceRange')}
                id="dining-details-priceRange"
                name="priceRange"
                data-cy="priceRange"
                type="text"
              />
              <ValidatedField
                label={translate('activitiesApp.diningDetails.features')}
                id="dining-details-features"
                name="features"
                data-cy="features"
                type="text"
              />
              <ValidatedField
                label={translate('activitiesApp.diningDetails.specialDiets')}
                id="dining-details-specialDiets"
                name="specialDiets"
                data-cy="specialDiets"
                type="text"
              />
              <ValidatedField
                label={translate('activitiesApp.diningDetails.notes')}
                id="dining-details-notes"
                name="notes"
                data-cy="notes"
                type="text"
              />
              <ValidatedField
                label={translate('activitiesApp.diningDetails.createdDate')}
                id="dining-details-createdDate"
                name="createdDate"
                data-cy="createdDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('activitiesApp.diningDetails.modifiedDate')}
                id="dining-details-modifiedDate"
                name="modifiedDate"
                data-cy="modifiedDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('activitiesApp.diningDetails.mealType')}
                id="dining-details-mealType"
                data-cy="mealType"
                type="select"
                multiple
                name="mealTypes"
              >
                <option value="" key="0" />
                {mealTypes
                  ? mealTypes.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.mealTypeName}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('activitiesApp.diningDetails.cusine')}
                id="dining-details-cusine"
                data-cy="cusine"
                type="select"
                multiple
                name="cusines"
              >
                <option value="" key="0" />
                {cusines
                  ? cusines.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.cusineName}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/dining-details" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default DiningDetailsUpdate;
